import React, { useContext, useEffect, useState } from 'react';
import { makeStyles, withTheme } from '@mui/styles';
import { ConfirmationModal, useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { EditorContext } from 'studio/pages/Editor/Editor';
import { CONCEPT_PASSAGE_TYPE_STRING, useEditorUtils } from 'studio/pages/Editor/EditorUtils';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import ErrorIcon from '@mui/icons-material/ErrorRounded';
import WarningIcon from '@mui/icons-material/WarningRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import FilterListIcon from '@mui/icons-material/FilterListRounded';
import StorageIcon from '@mui/icons-material/StorageRounded';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/PersonRounded';
import DashboardIcon from '@mui/icons-material/DashboardRounded';
import WidgetsIcon from '@mui/icons-material/WidgetsRounded';
import ShareIcon from '@mui/icons-material/ShareRounded';
import PeopleAltIcon from '@mui/icons-material/PeopleAltRounded';
import CodeIcon from '@mui/icons-material/Code';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Accordion from 'studio/components/Accordion';
import { pluralize as plrl } from 'studio/helpers';
import Alert from 'studio/components/Alert';
import { ToolsIcon } from 'components/Icons';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import services from 'services';
import Progress from 'studio/components/Progress';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import Chip from 'studio/components/Chip/Chip';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import Link from 'studio/components/Link';
import { Virtuoso } from 'react-virtuoso';
import Tab from '@mui/material/Tab';
import Tabs from 'studio/components/Tabs/Tabs';
import { buildStudioLink } from 'components/buttons/JumpToStudio';

const flexColumn = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
};
const useStyles = makeStyles(theme => ({
  flexColumn,
  root: {
    '& .MuiDialogContent-root': {
      ...flexColumn
    },
    // Virtuoso wraps the accordions in divs, breaking the styling of consecutive accordions, so we manually recreate it
    '& *[data-testid="virtuoso-item-list"]': {
      '& > *:not(:last-child) .MuiAccordion-root': {
        borderBottom: 0
      },
      '& .MuiAccordion-root': {
        borderRadius: 0
      },
      '& > *:first-child .MuiAccordion-root': {
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4
      },
      '& > *:last-child .MuiAccordion-root': {
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4
      }
    }
  },
  successAlert: {
    marginTop: theme.spacing(1)
  },
  accordion: {
    '& .MuiAccordionSummary-content': {
      '& > .MuiSvgIcon-root': {
        marginRight: theme.spacing(1)
      }
    },
    '& .MuiAccordionSummary-expandIcon': {
      // with the default (negative) margin, a horizontal scrollbar is briefly shown during the button transition (rotation)
      marginRight: -4
    }
  },
  referencesList: {
    padding: 0,
    '& .MuiListItem-root': {
      padding: 0,
      height: 30,
      borderRadius: 2,
      '& > *:first-child': {
        flex: 1
      },
      '& .MuiIconButton-root': {
        marginLeft: theme.spacing(1.5)
      }
    }
  },
  repairDialog: {
    '& .MuiPaper-root': {
      width: '100%',
      maxWidth: 900
    }
  },
  errorsContainer: {
    marginBottom: theme.spacing(2),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1)
    },
    '& .MuiAccordion-root': {
      '& .MuiAccordionSummary-root': {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        minHeight: 31
      }
    },
    '& .MuiChip-root': {
      '&:not(:first-child)': {
        marginLeft: 5
      },
      '&:not(:last-child)': {
        marginRight: 5
      }
    }
  },
  vqlTextField: {
    margin: 0,
    marginBottom: theme.spacing(1),
    '& textarea': {
      fontSize: 12,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace'
    }
  },
  resultContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  answerContainer: {
    flex: 1
  },
  answerInfoContainer: {
    display: 'flex',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(2)
    },
    '& .MuiTypography-h4': {
      marginTop: 0,
      marginBottom: theme.spacing(1)
    }
  },
  answerTitle: {
    minWidth: 160
  },
  answerFilters: {
    margin: -2,
    marginTop: -4,
    '& .MuiChip-root': {
      margin: 2
    }
  },
  dataGrid: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      paddingLeft: 0
    },
    '& .MuiDataGrid-cell': {
      fontSize: 13
    },
    '& .MuiDataGrid-footerContainer': {
      minHeight: 0
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: 22
    },
    '& .MuiTablePagination-caption': {
      fontSize: 12
    },
    '& .MuiTablePagination-actions': {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      '& .MuiIconButton-root': {
        padding: 0,
        '& .MuiSvgIcon-root': {
          width: 18,
          height: 18
        }
      }
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'right',
    marginBottom: theme.spacing(1),
    '& > *': {
      marginLeft: theme.spacing(2)
    }
  }
}));

const getFormattedRuleKind = ruleKind => {
  if (ruleKind === 'VQL_RULE') {
    return 'Rewriting rule';
  }
  const withSpaces = ruleKind.replace(/_/g, ' ');

  if (withSpaces.length === 0) {
    return withSpaces;
  }

  if (withSpaces.length === 1) {
    return withSpaces.toUpperCase();
  }

  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

const FailureReference = ({ knowledgeGraph, failureReference }) => {
  const useStyles = makeStyles(theme => ({
    root: {
      display: 'flex',
      alignItems: 'center'
    },
    type: {
      alignSelf: 'stretch',
      display: 'flex',
      alignItems: 'center',
      fontWeight: 800,
      marginRight: 6,
      '& .MuiSvgIcon-root': {
        width: 16,
        height: 16,
        marginRight: 6
      }
    },
    label: {
      flex: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginRight: theme.spacing(1),
      '& .MuiLink-root': {
        display: 'inline-flex',
        alignItems: 'center',
        '& .MuiSvgIcon-root': {
          width: 11,
          height: 11,
          marginRight: 4
        }
      }
    },
    additionalInfo: {
      '& .MuiChip-root': {
        height: 22,
        marginRight: 3,
        '&:last-child': {
          marginRight: 0
        },
        '& .MuiChip-icon': {
          width: 16,
          height: 16
        },
        '& .MuiChip-label': {
          fontSize: 12
        }
      }
    }
  }));
  const classes = useStyles();

  let TypeIcon = null;
  let type = '';
  let label = '';
  const additionalInfo = [];

  switch (failureReference.data.type) {
    case 'Widget':
      TypeIcon = WidgetsIcon;
      type = 'Widget';
      label = failureReference.data.widgetTitle;
      additionalInfo.push([DashboardIcon, failureReference.data.boardName]);
      break;
    case 'CustomerSelection':
      TypeIcon = PeopleAltIcon;
      type = 'Customer selection';
      label = failureReference.data.customerSelectionName;
      break;
    case 'SharedAnswer':
      TypeIcon = ShareIcon;
      type = 'Shared answer';
      label = (
        <Link href={failureReference.data.sharedAnswerUrl} newWindow>
          <OpenInNewIcon fontSize="small" />
          {failureReference.data.sharedAnswerQuestionText || failureReference.data.sharedAnswerId}
        </Link>
      );
      break;
    case 'Rule':
      TypeIcon = CodeIcon;
      type = getFormattedRuleKind(failureReference.data.ruleKind);
      label = (
        <Link
          href={buildStudioLink(
            knowledgeGraph.data.id,
            failureReference.data.filePath,
            failureReference.data.vklIdentifier
          )}
          newWindow
        >
          <OpenInNewIcon fontSize="small" />
          {/* use VKL identifier instead of rule name, since VKL identifier is more precise w.r.t. the link */}
          {failureReference.data.vklIdentifier}
        </Link>
      );
      break;
  }

  if (failureReference.data.type !== 'Rule') {
    additionalInfo.push([PersonIcon, failureReference.data.userFullName]);
  }

  return (
    <div className={classes.root}>
      <div className={classes.type}>
        {!!TypeIcon && <TypeIcon />} {type}
      </div>
      <div className={classes.label}>{label}</div>
      <div className={classes.additionalInfo}>
        {additionalInfo.map(([Icon, label], index) => (
          <Chip key={index} size="small" icon={<Icon />} label={label} />
        ))}
      </div>
    </div>
  );
};

/**
 * Modal that displays the results of a test run
 *
 * @param theme the theme to use (provided by MUI)
 * @param testResult the test result to display
 * @param isOpen whether the modal should be displayed
 * @param onConfirm callback for when the user confirms the test results, will display a confirm button if provided
 *                  It is passed a boolean indicating whether the tests are considered to have passed.
 * @param onClose callback for when the user closes the modal (should change `isOpen`)
 * @param confirmButtonText text for the confirm button
 */
const TestResultModal = ({ theme, testResult, isOpen, onConfirm, confirmButtonText, onClose }) => {
  const { knowledgeGraph } = useContext(EditorContext);
  const { showConfirmationModal } = useConfirmationModal();
  const { withLoader } = useLoader();
  const { successMessage, errorMessage } = useMessage();

  const { createKgMessage } = useEditorUtils();
  const classes = useStyles();

  // stores the index of the currently selected tab
  const [tabIndex, setTabIndex] = useState(0);
  // stores the keys (failure message texts) of the expanded accordions
  const [expanded, setExpanded] = useState([]);
  // contains the interpretation to repair, its failure messages, main KG failure messages and references
  const [repairContext, setRepairContext] = useState(null);
  // the editable, new VQL for the interpretation
  const [vql, setVql] = useState(null);
  // the result of running the interpretation with the new VQL: either an answer or an error object
  const [runResult, setRunResult] = useState(null);
  // stores the IDs of the interpretations that have been newly repaired in the current test/repair session
  // note: we currently do not support repairing rules (e.g., definitions)
  const [newlyRepairedInterpretationIds, setNewlyRepairedInterpretationIds] = useState([]);

  const isDevKg = knowledgeGraph?.data.developmentKgForId;

  // update the VQL whenever the interpretation to repair changes
  useEffect(() => {
    if (repairContext) {
      // fall back to the approximate VQL if the interpretation's VQL is not available
      const vql = repairContext.interpretation.data.vql || repairContext.interpretation.data.oldVql || '';
      setVql(vql);
    }
  }, [repairContext]);

  const openRepairDialog = (
    interpretation,
    isRepaired,
    failureMessages,
    mainKgFailureMessages,
    interpretationReferences
  ) => {
    setRunResult(null);
    setRepairContext({
      interpretation,
      isRepaired,
      failureMessages,
      mainKgFailureMessages,
      interpretationReferences
    });
  };

  const closeRepairDialog = () => setRepairContext(null);

  const getInterpretation = async interpretationId => {
    const response = await withLoader(services.getInterpretation(knowledgeGraph.data.id, interpretationId));
    return response.data;
  };

  const runInterpretation = async interpretation => {
    setRunResult(undefined);
    const { id } = interpretation.data;
    const response = await withLoader(services.runInterpretation(knowledgeGraph.data.id, id, vql));
    const result = response.data || response.response.data;
    setRunResult(result);
  };

  const saveVql = async (interpretation, vql) => {
    const { id } = interpretation.data;
    const response = await withLoader(services.updateInterpretation(knowledgeGraph.data.id, id, vql));
    if (response.success) {
      successMessage('The VQL has successfully been saved');
      setNewlyRepairedInterpretationIds(ids => [...ids, id]);
      setRepairContext(null);
    } else {
      const message = response.response.data.data?.message || 'There was an error saving the VQL';
      errorMessage(message);
    }
  };

  const numberOfTestedInterpretations = testResult?.numberOfTestedInterpretations || 0;
  const numberOfTestedRules = testResult?.numberOfTestedRules || 0;
  const testFailures = testResult?.failures || [];
  const isSuccess = testFailures.length === 0;

  // returns a value to sort the specified failure reference by
  const toSortValue = failureReference => {
    switch (failureReference.data.type) {
      case 'Widget':
        return 1 + failureReference.data.widgetTitle;
      case 'CustomerSelection':
        return 2 + failureReference.data.customerSelectionName;
      case 'SharedAnswer':
        return 3 + failureReference.data.sharedAnswerQuestionText;
      case 'Rule':
        // include ruleKind first to make sure all rules of the same kind are grouped together
        return 4 + failureReference.data.ruleKind + failureReference.data.ruleName;
    }
  };

  const getFailureMessageToDisplay = fullFailureMessage => {
    if (!fullFailureMessage) {
      return '';
    }
    // only show first line to avoid having too large messages
    const lines = fullFailureMessage.split('\n');
    return lines.length > 1 ? `${lines[0]} ...` : lines[0];
  };

  const testFailureIsInterpretation = testFailure => testFailure.data?.testedObject?.data?.type === 'Interpretation';

  // should only invoke this if the test failure is an interpretation
  const getInterpretationRefsFromTestFailure = testFailure =>
    testFailure.data?.testedObject?.data?.interpretationReferences;

  // checks whether the specified test failure has been repaired
  const testFailureIsRepaired = testFailure =>
    // the test failure has already previously been repaired
    testFailure.data.isRepaired ||
    // the test failure has been newly repaired in the current test/repair session
    newlyRepairedInterpretationIds.includes(testFailure.data.failureId);

  // checks whether the specified failure group has been repaired
  const failureGroupIsRepaired = failureGroup => failureGroup.testFailures.every(testFailureIsRepaired);

  // group the test failures by failure message and partition the failure groups into:
  //   - failure groups in the normal KG or, if applicable, dev KG
  //   - if applicable, failure groups in main KG
  const [failureGroups, mainKgFailureGroups] = _.partition(
    // group the test failures by the failure message default text
    Object.values(_.groupBy(testFailures, testFailure => testFailure.data.failureMessage.defaultText)),
    // attribute a group of test failures to the main KG if all of them already fail in the main KG
    testFailures => !testFailures.every(testFailure => testFailure.data.alreadyFailsInMainKg)
  )
    // combine the group's failure message and test failures in an object
    .map(groupedTestFailures =>
      groupedTestFailures.map(testFailures => ({
        failureMessage: testFailures[0].data.failureMessage,
        testFailures
      }))
    );

  // the number of test failures in the normal (or dev, if applicable) KG and in the main KG (if applicable)
  const [[testFailuresCount, repairedTestFailuresCount], [mainKgTestFailuresCount, repairedMainKgTestFailuresCount]] = [
    failureGroups,
    mainKgFailureGroups
  ].map(failureGroups => [
    _.sum(failureGroups.map(failureGroup => failureGroup.testFailures.filter(tf => !testFailureIsRepaired(tf)).length)),
    _.sum(failureGroups.map(failureGroup => failureGroup.testFailures.filter(testFailureIsRepaired).length))
  ]);

  // expand the first failure groups if they're the only ones
  useEffect(() => {
    if (testResult) {
      if (failureGroups.length === 1 && !failureGroupIsRepaired(failureGroups[0])) {
        setExpanded(expanded => [...expanded, failureGroups[0].failureMessage.defaultText]);
      }
      if (mainKgFailureGroups.length === 1 && !failureGroupIsRepaired(mainKgFailureGroups[0])) {
        setExpanded(expanded => [...expanded, mainKgFailureGroups[0].failureMessage.defaultText]);
      }
    }
  }, [testResult]);

  // Callback for when a passage is clicked
  const passageClickCallback = passage => {
    // Close the test modal when navigating to a concept in the KG (so that the code is visible)
    // Other kinds of clickable passages are not expected currently and not handled
    if (passage.type === CONCEPT_PASSAGE_TYPE_STRING && !passage.isBroken) {
      onClose();
    }
  };

  // returns the correct color for a (potentially repaired) error or warning
  const getColor = (isError, isRepaired) =>
    isRepaired ? theme.palette.success.main : isError ? theme.palette.error.main : theme.palette.warning.main;

  // Gets an error or warning icon, in the correct color
  const getFailureIcon = (isError, isRepaired, fontSize = 'default', color) => {
    const Icon = isError ? ErrorIcon : WarningIcon;
    return <Icon style={{ color: color !== undefined ? color : getColor(isError, isRepaired) }} fontSize={fontSize} />;
  };

  const renderFailureGroup = ({ failureMessage, testFailures }, isError) => {
    const failureMessageText = failureMessage.defaultText;

    // Create a failure message that cleans up the text that will be displayed to the user (e.g., shortens it)
    const failureMessageToDisplay = {
      ...failureMessage,
      defaultText: getFailureMessageToDisplay(failureMessage.defaultText),
      passages: failureMessage.passages.map(passage => ({
        ...passage,
        text: passage.text && getFailureMessageToDisplay(passage.text)
      }))
    };

    // flatten and sort the interpretation references of the test failures
    const unsortedFailureReferences = testFailures.flatMap(testFailure => {
      return testFailureIsInterpretation(testFailure)
        ? getInterpretationRefsFromTestFailure(testFailure)
        : [testFailure.data.testedObject]; // else case: rule is failing --> we just include the rule itself
    });

    const failureReferences = _.sortBy(unsortedFailureReferences, toSortValue);
    // the IDs of all repaired interpretations in the failure group
    const repairedInterpretationIds = testFailures
      .filter(testFailureIsRepaired)
      .map(testFailure => testFailure.data.failureId);
    // check if all test failures in the failure group have been repaired
    // we currently only support repairing interpretations, not rules
    const failureGroupIsRepaired = testFailures.every(
      testFailure =>
        // we could omit the first conjunct since repairedInterpretationIds should include only interpretations, but
        // include it here to be sure that only interpretations are included
        testFailureIsInterpretation(testFailure) && repairedInterpretationIds.includes(testFailure.data.failureId)
    );

    return (
      <Accordion
        expanded={expanded.includes(failureMessageText)}
        onChange={(event, isExpanded) =>
          // remove key, then add it back in if 'isExpanded' is true
          setExpanded(expanded =>
            expanded.filter(key => key !== failureMessageText).concat(isExpanded ? [failureMessageText] : [])
          )
        }
        className={classes.accordion}
        key={failureMessageText}
        color={getColor(isError, failureGroupIsRepaired)}
        summary={
          <>
            {getFailureIcon(isError, failureGroupIsRepaired)}
            <span>{createKgMessage(failureMessageToDisplay, passageClickCallback)}</span>
          </>
        }
        // unmount the accordion details when it is collapsed: this makes scrolling smoother when there are many accordions
        // (cf. also https://v4.mui.com/components/accordion/#performance)
        TransitionProps={{ unmountOnExit: true }}
        data-test-variant={failureGroupIsRepaired ? 'repaired' : isError ? 'error' : 'warning'}
      >
        <List dense className={classes.referencesList} style={{ flex: 1, minWidth: 0 }}>
          {failureReferences.map((failureReference, index) => {
            const isInterpretation = failureReference.data.type !== 'Rule';
            const { interpretationId } = failureReference.data;
            // note: interpretationId will be defined only for interpretation references
            // we log an error in case this property does not hold
            if (isInterpretation && interpretationId === undefined) {
              console.error('Interpretation reference does not have an interpretation id.');
            } else if (!isInterpretation && interpretationId !== undefined) {
              console.error('Rule has an interpretation id but should not have one.');
            }
            const isRepaired = isInterpretation && repairedInterpretationIds.includes(interpretationId);
            return (
              <ListItem key={index}>
                <div style={{ minWidth: 0 }}>
                  <FailureReference knowledgeGraph={knowledgeGraph} failureReference={failureReference} />
                </div>
                {isInterpretation && (
                  <Tooltip
                    title={isRepaired ? 'The answer is repaired, click to edit it' : 'Click to repair the answer'}
                    placement="left"
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={async () => {
                        // checks if the specified test failure matches the interpretation of the reference
                        const matchesInterpretation = testFailure => testFailure.data.failureId === interpretationId;

                        // retrieve the interpretation of the reference
                        const interpretation = await getInterpretation(interpretationId);
                        if (interpretation) {
                          // the failure messages the normal (or dev, if applicable) KG and in the main KG (if applicable)
                          const [failureMessages, mainKgFailureMessages] = [failureGroups, mainKgFailureGroups].map(
                            failureGroups =>
                              failureGroups
                                .filter(failureGroup => failureGroup.testFailures.some(matchesInterpretation))
                                .map(failureGroup => failureGroup.failureMessage)
                          );
                          // all references of the interpretation
                          const interpretationReferences = getInterpretationRefsFromTestFailure(
                            testFailures.find(matchesInterpretation)
                          );
                          openRepairDialog(
                            interpretation,
                            isRepaired,
                            failureMessages,
                            mainKgFailureMessages,
                            interpretationReferences
                          );
                        } else {
                          errorMessage('There was an error retrieving the saved answer');
                        }
                      }}
                    >
                      {isRepaired ? (
                        <CheckCircleIcon fontSize="inherit" style={{ color: theme.palette.success.main }} />
                      ) : (
                        <ToolsIcon fontSize="inherit" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
            );
          })}
        </List>
      </Accordion>
    );
  };

  const renderFailureGroups = (
    failureGroups,
    isError,
    testFailuresCount,
    repairedTestFailuresCount,
    type,
    text,
    dataTest
  ) => (
    <div className={classes.flexColumn} data-test={dataTest}>
      <Typography variant="body1" gutterBottom>
        There {plrl(testFailuresCount, 'is', 'are')}
        <b>
          {' '}
          {testFailuresCount || 'no'} {plrl(testFailuresCount, `${type} error`)}{' '}
        </b>
        {repairedTestFailuresCount ? ` (${repairedTestFailuresCount} repaired) ` : ''}
        {text}
      </Typography>
      <div className={classes.flexColumn}>
        <Virtuoso
          style={{ flex: 1 }}
          data={failureGroups}
          itemContent={(index, failureGroup) => renderFailureGroup(failureGroup, isError)}
        />
      </div>
    </div>
  );

  const testResultModalContent = (
    <div className={classes.flexColumn}>
      <Typography variant="body1" style={{ marginBottom: theme.spacing(1) }}>
        {numberOfTestedInterpretations + numberOfTestedRules > 0 ? (
          <>
            <b>
              {numberOfTestedInterpretations} {plrl(numberOfTestedInterpretations, 'saved answer')}
            </b>
            {' and '}
            <b>
              {numberOfTestedRules} {plrl(numberOfTestedRules, 'rule')}
            </b>{' '}
            {plrl(numberOfTestedInterpretations + numberOfTestedRules, 'has', 'have')} been tested. Saved answers are
            widgets, customer selections or shared answers. Rules are default filters, definitions, or rewriting rules.
            {isDevKg ? 'These are both from the development and the main knowledge graph.' : ''}
          </>
        ) : (
          <>There are no saved answers or rules to test.</>
        )}
      </Typography>
      {!isSuccess && (
        <>
          <Tabs value={tabIndex} onChange={(_, index) => setTabIndex(index)}>
            <Tab
              label={
                <div>
                  {getFailureIcon(true, false, 'default', null)}
                  {isDevKg ? 'New errors' : 'Errors'} ({testFailuresCount})
                </div>
              }
            />
            {isDevKg && (
              <Tab
                label={
                  <div>
                    {getFailureIcon(false, false, 'default', null)}
                    Existing errors ({mainKgTestFailuresCount})
                  </div>
                }
              />
            )}
          </Tabs>
          <div className={classes.flexColumn} style={tabIndex === 0 ? {} : { display: 'none' }}>
            {failureGroups.length > 0 ? (
              renderFailureGroups(
                failureGroups,
                true,
                testFailuresCount,
                repairedTestFailuresCount,
                isDevKg ? 'new' : '',
                isDevKg
                  ? "due to the changes in the development knowledge graph. You need to fix these errors before merging, so you don't break existing saved answers or rules with your changes."
                  : 'in the knowledge graph.',
                isDevKg ? 'newErrors' : 'errors'
              )
            ) : (
              <>
                There are no {isDevKg ? 'new' : ''} errors{' '}
                {isDevKg ? 'due to the changes in the development' : 'in the'} knowledge graph.
              </>
            )}
          </div>
          <div className={classes.flexColumn} style={tabIndex === 1 ? {} : { display: 'none' }}>
            {mainKgFailureGroups.length > 0 ? (
              renderFailureGroups(
                mainKgFailureGroups,
                false,
                mainKgTestFailuresCount,
                repairedMainKgTestFailuresCount,
                'existing',
                'in the main knowledge graph. These errors are already in the main knowledge graph and are not related to your changes. You should look into them at some point and fix them.',
                'existingErrors'
              )
            ) : (
              <>There are no existing errors in the main knowledge graph.</>
            )}
          </div>
        </>
      )}

      {isSuccess && (
        <Alert severity="success" className={classes.successAlert}>
          No failures were detected in the knowledge graph
        </Alert>
      )}
    </div>
  );

  // the columns of the answer's data
  const answerDataColumns = runResult?.data.data?.columns.map(column => ({
    field: column.identifier,
    headerName: column.title,
    flex: 1
  }));

  // the rows of the answer's data
  const answerDataRows = runResult?.data.data?.data.map((row, index) => ({
    id: index,
    ...row
  }));

  const dataGridPageSize = 5;
  const dataGridHeaderHeight = 28;
  const dataGridRowHeight = 22;
  // top and bottom border + header height + height of rows and footer
  const dataGridHeight =
    2 + dataGridHeaderHeight + (Math.min(answerDataRows?.length, dataGridPageSize) + 1) * dataGridRowHeight;

  return (
    <>
      <ConfirmationModal
        open={isOpen}
        title="Saved Answers and Rules Test Results"
        maxWidth={isSuccess ? 'sm' : 'md'}
        // Fix height so that modal height does not change when expanding a failure
        fixedHeight={isSuccess ? undefined : '80vh'}
        onClose={onClose}
        onConfirm={
          onConfirm &&
          (() => {
            const allFailureGroupsAreRepaired = failureGroups.every(failureGroupIsRepaired);
            onConfirm(allFailureGroupsAreRepaired);
          })
        }
        confirmButtonText={confirmButtonText}
        className={classes.root}
      >
        {testResultModalContent}
      </ConfirmationModal>
      <Dialog
        className={classes.repairDialog}
        open={repairContext !== null}
        onClose={closeRepairDialog}
        data-test="repairDialog"
      >
        <DialogTitle>{repairContext?.isRepaired ? 'Edit' : 'Repair'} answer</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Modify the VQL of the answer to {repairContext?.isRepaired ? 'edit' : 'repair'} it, then test and save the
            changes.
          </Typography>

          <Typography variant="h3" gutterBottom style={{ marginTop: 0 }}>
            {}
            {plrl(
              repairContext?.failureMessages.length + repairContext?.mainKgFailureMessages.length,
              repairContext?.isRepaired ? 'Repaired error' : 'Error'
            )}
          </Typography>
          <div className={classes.errorsContainer}>
            {repairContext?.failureMessages.length > 0 && (
              <div>
                {repairContext?.failureMessages.map((failureMessage, index) => (
                  <Accordion
                    key={index}
                    className={classes.accordion}
                    color={getColor(true, repairContext?.isRepaired)}
                    summary={
                      <>
                        {getFailureIcon(true, repairContext?.isRepaired, 'small')}
                        <span>{createKgMessage(failureMessage, passageClickCallback)}</span>
                      </>
                    }
                  />
                ))}
              </div>
            )}
            {repairContext?.mainKgFailureMessages.length > 0 && (
              <div>
                {repairContext?.mainKgFailureMessages.map((failureMessage, index) => (
                  <Accordion
                    key={index}
                    className={classes.accordion}
                    color={getColor(false, repairContext?.isRepaired)}
                    summary={
                      <>
                        {getFailureIcon(false, repairContext?.isRepaired, 'small')}
                        <span>{createKgMessage(failureMessage, passageClickCallback)}</span>
                      </>
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <Typography variant="h3" gutterBottom>
            {}
            {plrl(repairContext?.interpretationReferences, 'Reference')}
          </Typography>
          <List dense className={classes.referencesList}>
            {_.sortBy(repairContext?.interpretationReferences, toSortValue).map((interpretationReference, index) => (
              <ListItem key={index}>
                <FailureReference knowledgeGraph={knowledgeGraph} failureReference={interpretationReference} />
              </ListItem>
            ))}
          </List>
          <Typography variant="h3" gutterBottom>
            VQL
          </Typography>
          <TextField
            className={classes.vqlTextField}
            size="small"
            value={vql}
            onChange={event => setVql(event.target.value)}
            multiline
            fullWidth
            minRows={Math.min(8, Math.max(vql?.split(/\r\n|\r|\n/).length, 3))}
            maxRows={8}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={async () => await runInterpretation(repairContext?.interpretation)}
          >
            Test
          </Button>

          <div className={classes.resultContainer}>
            {runResult === undefined && <Progress />}
            {!!runResult && runResult.type === 'Answer' && (
              <>
                <Accordion
                  className={classes.accordion}
                  defaultExpanded
                  color={theme.palette.success.main}
                  summary={
                    <>
                      <CheckCircleOutlineIcon style={{ color: theme.palette.success.main }} />
                      <span>
                        Running the answer succeeded: before saving the changes, verify that the answer is correct
                      </span>
                    </>
                  }
                  data-test="testSuccessAccordion"
                >
                  <div className={classes.answerContainer}>
                    <div className={classes.answerInfoContainer}>
                      <div>
                        <Typography variant="h4">
                          <InfoIcon /> Title
                        </Typography>
                        <div className={classes.answerTitle}>{runResult.data.title}</div>
                      </div>
                      <div>
                        <Typography variant="h4">
                          <FilterListIcon /> Filters
                        </Typography>
                        <div className={classes.answerFilters}>
                          {runResult.data.filters.map((filter, index) => (
                            <Chip key={index} size="small" label={filter} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <Typography variant="h4" gutterBottom>
                      <StorageIcon /> Data
                    </Typography>
                    <DataGrid
                      className={classes.dataGrid}
                      columns={answerDataColumns}
                      rows={answerDataRows}
                      disableColumnMenu
                      autoHeight={false}
                      height={dataGridHeight}
                      headerHeight={dataGridHeaderHeight}
                      rowHeight={dataGridRowHeight}
                      pageSize={dataGridPageSize}
                      disableSelectionOnClick
                    />
                  </div>
                </Accordion>
              </>
            )}
            {!!runResult && runResult.type === 'Error' && (
              <Accordion
                className={classes.accordion}
                color={getColor(true, false)}
                summary={
                  <>
                    {getFailureIcon(true, false)}
                    <span>{runResult.data.message}</span>
                  </>
                }
                data-test="testFailureAccordion"
              />
            )}
          </div>
          <div className={classes.actions}>
            <Button onClick={closeRepairDialog}>Close</Button>
            <Tooltip
              title={runResult?.type === 'Answer' ? '' : 'Fix and test the VQL before saving'}
              placement="top"
              arrow
            >
              <div>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={runResult?.type !== 'Answer'}
                  onClick={async () => {
                    showConfirmationModal(() => ({
                      title: 'Save VQL',
                      content: 'Do you really want to save the VQL?',
                      confirmButtonText: 'Save',
                      onConfirm: async () => await saveVql(repairContext?.interpretation, vql)
                    }));
                  }}
                >
                  Save
                </Button>
              </div>
            </Tooltip>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withTheme(TestResultModal);
